import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormControl, FormControlLabel, GlobalStyles, Radio, RadioGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import { getActiveLanguage } from '@haldor/ui';
import { getNormalizedActiveLanguage, getPickersLocale } from '../../../helpers/locale';

/**
 * StartEndInputs
 * Props:
 * - input: { name, value, onChange } (react-final-form) OR you can pass value/onChange directly
 * - name
 * - value: { start: string, end: string }
 * - onChange: function(updatedValue)
 * - disabled: boolean
 * - labels: { startLabel, endLabel } optional
 * - includeTime: boolean (renders date and time picker when true)
 * - error: boolean (shows red border when true)
 */
var StartEndInputs = function StartEndInputs(props) {
  var input = props.input,
    value = props.value,
    onChange = props.onChange,
    disabled = props.disabled,
    labels = props.labels,
    includeTime = props.includeTime,
    error = props.error,
    translate = props.translate;
  var current = input && input.value || value || {
    start: '',
    end: ''
  };
  var defaultEndTime = '23:59';
  var t = function t(key, fallback) {
    return translate ? translate(key) || fallback : fallback;
  };
  var hasStoredTime = function hasStoredTime(raw) {
    if (!raw || typeof raw !== 'string') return false;
    return raw.includes('T') || /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw);
  };
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    useTime = _useState2[0],
    setUseTime = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isEndTimeManuallyChanged = _useState4[0],
    setIsEndTimeManuallyChanged = _useState4[1];
  var timeModeInitializedRef = React.useRef(false);
  var emitChange = function emitChange(next) {
    if (input && input.onChange) {
      input.onChange(next);
    }
    if (onChange) onChange(next);
  };
  var updateRange = function updateRange(nextValues) {
    emitChange(_objectSpread(_objectSpread({}, current || {
      start: '',
      end: ''
    }), nextValues));
  };
  var setField = function setField(field, v) {
    updateRange(_defineProperty({}, field, v));
  };
  var finalStartLabel = labels && labels.startLabel || 'Start Date';
  var finalEndLabel = labels && labels.endLabel || 'End Date';
  var PickerComponent = useTime ? DateTimePicker : DatePicker;
  var parseValue = function parseValue(raw) {
    if (!raw) return null;
    var parsedISO = Moment(raw, Moment.ISO_8601, true);
    if (parsedISO.isValid()) return parsedISO;
    var parsedDate = Moment(raw, 'YYYY-MM-DD', true);
    return parsedDate.isValid() ? parsedDate : null;
  };

  // Always emit UTC ISO with timezone (consistent with other submit flows, e.g. ReportAbsence).
  var formatValue = function formatValue(date) {
    if (!date) return '';
    return Moment.utc(date).format();
  };
  var setEndOfDay = function setEndOfDay(date) {
    return date.clone().local().set({
      hour: 23,
      minute: 59,
      second: 0,
      millisecond: 0
    });
  };
  var toEndOfDayWithTimezone = function toEndOfDayWithTimezone(raw) {
    var parsed = parseValue(raw);
    if (!parsed) return '';
    return Moment.utc(setEndOfDay(parsed)).format();
  };
  var localDateKey = function localDateKey(momentValue) {
    return momentValue ? momentValue.clone().local().format('YYYY-MM-DD') : '';
  };
  var localTimeKey = function localTimeKey(momentValue) {
    return momentValue ? momentValue.clone().local().format('HH:mm') : '';
  };

  // Initialize time mode once based on whether the initial values have time
  useEffect(function () {
    if (!includeTime) {
      setUseTime(false);
      setIsEndTimeManuallyChanged(false);
      timeModeInitializedRef.current = false;
      return;
    }
    if (!timeModeInitializedRef.current) {
      var shouldUseTime = hasStoredTime(current.start) || hasStoredTime(current.end);
      setUseTime(shouldUseTime);
      timeModeInitializedRef.current = true;
    }
  }, [includeTime]);
  useEffect(function () {
    if (!useTime || isEndTimeManuallyChanged || !current.end) return;
    var parsedEnd = parseValue(current.end);
    if (!parsedEnd) return;
    var localTime = parsedEnd.clone().local().format('HH:mm');
    if (localTime !== defaultEndTime) {
      updateRange({
        end: toEndOfDayWithTimezone(current.end)
      });
    }
  }, [useTime, isEndTimeManuallyChanged, current.end]);
  var handleTimeModeChange = function handleTimeModeChange(event) {
    var nextUseTime = event.target.value === 'true';
    setUseTime(nextUseTime);
    setIsEndTimeManuallyChanged(false);
    if (!nextUseTime) {
      var parsedStart = parseValue(current.start);
      var parsedEnd = parseValue(current.end);
      updateRange({
        start: parsedStart ? formatValue(parsedStart.clone().local().set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        })) : current.start,
        end: parsedEnd ? toEndOfDayWithTimezone(current.end) : current.end
      });
      return;
    }
    updateRange({
      end: toEndOfDayWithTimezone(current.end)
    });
  };
  var handleEndChange = function handleEndChange(date) {
    if (!date) {
      setField('end', '');
      setIsEndTimeManuallyChanged(false);
      return;
    }
    if (!useTime) {
      setField('end', formatValue(setEndOfDay(Moment(date))));
      return;
    }
    var nextMoment = Moment(date);
    var currentMoment = parseValue(current.end);
    var normalizedEndMoment = setEndOfDay(nextMoment);
    if (!isEndTimeManuallyChanged) {
      var sameDate = !!currentMoment && localDateKey(nextMoment) === localDateKey(currentMoment);
      var timeChangedOnSameDate = sameDate && localTimeKey(nextMoment) !== localTimeKey(currentMoment);
      var userChangedTime = timeChangedOnSameDate && localTimeKey(nextMoment) !== defaultEndTime;
      if (userChangedTime) {
        setIsEndTimeManuallyChanged(true);
        setField('end', formatValue(nextMoment));
        return;
      }
      setField('end', formatValue(normalizedEndMoment));
      return;
    }
    setField('end', formatValue(nextMoment));
  };
  var active = getActiveLanguage();
  Moment.locale(active);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GlobalStyles, {
    styles: {
      '.MuiPickersPopper-root': {
        zIndex: 1000000000 + ' !important'
      }
    }
  }), /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterMoment,
    adapterLocale: getNormalizedActiveLanguage(),
    localeText: function (_pl$components) {
      var pl = getPickersLocale(getNormalizedActiveLanguage());
      return (pl === null || pl === void 0 || (_pl$components = pl.components) === null || _pl$components === void 0 || (_pl$components = _pl$components.MuiLocalizationProvider) === null || _pl$components === void 0 || (_pl$components = _pl$components.defaultProps) === null || _pl$components === void 0 ? void 0 : _pl$components.localeText) || undefined;
    }()
  }, /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, includeTime && /*#__PURE__*/React.createElement(FormControl, {
    component: "fieldset",
    sx: {
      mb: 2
    },
    disabled: disabled
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    row: true,
    value: String(useTime),
    onChange: handleTimeModeChange
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "false",
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('Date', 'Date')
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "true",
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('Date and time', 'Date and time')
  }))), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    gap: 2,
    alignItems: "center",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: 1
  }, /*#__PURE__*/React.createElement(PickerComponent, {
    label: finalStartLabel,
    value: parseValue(current.start),
    onChange: function onChange(date) {
      if (!date) {
        setField('start', '');
        return;
      }
      var m = useTime ? date : date.clone().local().set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      setField('start', formatValue(m));
    },
    disabled: disabled,
    maxDate: parseValue(current.end) || undefined,
    slots: {
      textField: TextField
    },
    slotProps: {
      textField: {
        fullWidth: true,
        error: error
      }
    }
  })), /*#__PURE__*/React.createElement(Box, {
    flex: 1
  }, /*#__PURE__*/React.createElement(PickerComponent, {
    label: finalEndLabel,
    value: parseValue(current.end),
    onChange: handleEndChange,
    disabled: disabled,
    minDate: parseValue(current.start) || undefined,
    slots: {
      textField: TextField
    },
    slotProps: {
      textField: {
        fullWidth: true,
        error: error
      }
    }
  }))))));
};
export default StartEndInputs;